import { useEffect } from 'react';

/**
 * Keeps standalone mobile thread composer pinned to the visual viewport bottom
 * (stays above the on-screen keyboard while the page or URL bar moves).
 */
export function useStandaloneMobileThreadViewport(enabled) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const root = document.documentElement;
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = viewportMeta?.getAttribute('content') || '';
    root.classList.add('messages-standalone-mobile-vv');

    if (viewportMeta && !/interactive-widget/i.test(previousViewportContent)) {
      viewportMeta.setAttribute(
        'content',
        previousViewportContent
          ? `${previousViewportContent}, interactive-widget=resizes-content`
          : 'width=device-width, initial-scale=1, interactive-widget=resizes-content'
      );
    }

    const sync = () => {
      const keyboardGap = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
      root.style.setProperty('--messages-vv-offset-top', `${vv.offsetTop}px`);
      root.style.setProperty('--messages-vv-height', `${Math.max(vv.height, 280)}px`);
      root.style.setProperty('--messages-compose-vv-inset', `${keyboardGap}px`);
    };

    const onComposerFocus = (event) => {
      if (!event.target.closest?.('.messaging-compose')) return;
      sync();
      window.requestAnimationFrame(sync);
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    document.addEventListener('focusin', onComposerFocus);

    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      document.removeEventListener('focusin', onComposerFocus);
      root.classList.remove('messages-standalone-mobile-vv');
      if (viewportMeta && viewportMeta.getAttribute('content')?.includes('interactive-widget=resizes-content')) {
        viewportMeta.setAttribute('content', previousViewportContent);
      }
      root.style.removeProperty('--messages-vv-offset-top');
      root.style.removeProperty('--messages-vv-height');
      root.style.removeProperty('--messages-compose-vv-inset');
    };
  }, [enabled]);
}
